import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { LightBulbIcon } from '@heroicons/react/24/solid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { twMerge } from 'tailwind-merge';

import { useTheme } from '@/app/ThemeContext';
import { Dropdown } from '@/components/Dropdown';
import { Container } from '@/components/legacy-ui';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { Button } from '@/components/ui/button';
import {
  DisconnectIntegrationModal,
  IntegrationSetupModal,
} from '@/features/integrations/components';
import {
  getCategoryCounts,
  getCategoryIntegrations,
  getTotalIntegrationsCount,
  INTEGRATION_CATEGORIES,
} from '@/features/integrations/config/categories.config';
import { useFeatureFlag } from '@/features/integrations/hooks';
import { IntegrationCategory } from '@/features/integrations/types/category.types';
import type { SimpleIntegration } from '@/features/integrations/types/integration.types';
import { getIntegrationRegistry } from '@/features/integrations/utils';
import { HOTKEY_SCOPES } from '@/hooks/hotkeys';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useIntegration } from '@/hooks/useIntegration';
import SetupModal from '@/sections/SetupModal';
import WaitlistedModal from '@/sections/WaitlistModal';
import { useAuth } from '@/state/auth';
import { Account, Integration, IntegrationMeta } from '@/types';

export const IntegrationDrawer = ({
  initialCategory,
}: {
  initialCategory: string;
}) => {
  const { canManageIntegrations } = useEntitlements();
  const [search, setSearch] = useState('');
  const { currentTheme } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState<string>(
    initialCategory || 'All'
  );

  const [setupModalIntegration, setSetupModalIntegration] =
    useState<IntegrationMeta | null>(null);
  const [newSetupIntegration, setNewSetupIntegration] =
    useState<SimpleIntegration | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState<Account | null>(
    null
  );

  const {
    data: { connectedIntegrations },
  } = useIntegration();

  const { isDemoOrg } = useAuth();

  // Get registry instance
  const registry = getIntegrationRegistry();

  // Feature flags
  const useNewSystem = useFeatureFlag('USE_NEW_INTEGRATION_SYSTEM');
  const useNewOkta = useFeatureFlag(Integration.okta);
  const useNewTenableVM = useFeatureFlag(Integration.tenablevm);

  const categoryCounts = useMemo(() => getCategoryCounts(), []);
  const totalCount = useMemo(() => getTotalIntegrationsCount(), []);

  // Single source of truth for category ORDER. "All" is always first, the rest
  // are sorted alphabetically. Both the arrow-key navigation handler and the
  // sidebar render below iterate this exact array, so keyboard traversal order
  // always matches the visible order (ENG-43).
  const sortedCategories = useMemo(
    () => [
      'All',
      ...Object.keys(INTEGRATION_CATEGORIES).sort((a, b) => a.localeCompare(b)),
    ],
    []
  );

  const filteredIntegrations = useMemo(() => {
    if (selectedCategory === 'All') {
      return Object.entries(INTEGRATION_CATEGORIES).map(([category, _]) => ({
        category,
        integrations: getCategoryIntegrations(
          category as IntegrationCategory
        ).filter(integration =>
          integration?.name?.toLowerCase?.()?.includes(search.toLowerCase())
        ),
      }));
    }

    return [
      {
        category: selectedCategory,
        integrations: getCategoryIntegrations(
          selectedCategory as IntegrationCategory
        ).filter(integration =>
          integration?.name?.toLowerCase?.()?.includes(search.toLowerCase())
        ),
      },
    ];
  }, [selectedCategory, search]);

  const isIntegrationConnected = (integrationId: string) => {
    if (isDemoOrg && integrationId === 'crowdstrike') return true;
    return connectedIntegrations.some(conn => conn.member === integrationId);
  };

  const handleConnect = async (integration: IntegrationMeta) => {
    // Check if we should use new system for this integration
    const shouldUseNew =
      useNewSystem ||
      (integration.id === Integration.okta && useNewOkta) ||
      (integration.id === Integration.tenablevm && useNewTenableVM);

    if (shouldUseNew) {
      // Check if integration exists in new registry
      const newIntegration = registry.getIntegration(
        integration.id as Integration
      );
      if (newIntegration && newIntegration.type === 'simple') {
        setNewSetupIntegration(newIntegration as SimpleIntegration);
        return;
      }
    }

    // Fallback to old system
    setSetupModalIntegration(integration);
  };

  const handleSearchChange = (e: string) => {
    setSelectedCategory('All');
    setSearch(e);
  };

  // Drawer-scoped category navigation: ArrowUp / ArrowDown cycle the category
  // sidebar. Element-scoped via the RefCallback attached to the drawer root, so
  // arrows only move categories when focus is inside this drawer (and never
  // collide with the plain-arrow table navigation elsewhere). Scope `overlay`
  // because this lives in a drawer and must keep working while the drawer (an
  // overlay) is open — `global` would be suppressed. `selectedCategory` is in
  // deps so the handlers read the current selection.
  const hotkeyRef = useHotkeys<HTMLDivElement>(
    ['up', 'down'],
    event => {
      const index = sortedCategories.indexOf(selectedCategory);
      if (event.key === 'ArrowUp') {
        setSelectedCategory(
          index === 0
            ? sortedCategories[sortedCategories.length - 1]
            : sortedCategories[index - 1]
        );
      } else if (event.key === 'ArrowDown') {
        setSelectedCategory(
          index === sortedCategories.length - 1
            ? sortedCategories[0]
            : sortedCategories[index + 1]
        );
      }
    },
    {
      scopes: [HOTKEY_SCOPES.overlay],
      enableOnFormTags: false,
      preventDefault: true,
    },
    [selectedCategory, sortedCategories]
  );

  // The element-ref binding above only fires while focus is inside the drawer
  // root. Nothing else moves focus here on open, so without this the Arrow keys
  // are inert until the user clicks/Tabs in. Merge the `useHotkeys` RefCallback
  // with our own node ref (same pattern as useTableArrowNav) so we can both gate
  // the binding and focus the root on mount — satisfying the focus-within gate
  // immediately. `tabIndex={-1}` (set on the root below) makes the div
  // programmatically focusable without inserting it into the Tab order.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const setRootRef = useCallback<(node: HTMLDivElement | null) => void>(
    node => {
      hotkeyRef(node);
      rootRef.current = node;
    },
    [hotkeyRef]
  );

  // Defer one macrotask so the parent Drawer's [open] effect captures the opener
  // FIRST. React fires child mount effects before parent effects; a synchronous
  // focus here moves activeElement inside the drawer before Drawer.captureOpener
  // runs, so the opener is recorded null and close-time focus restoration is lost
  // (focus stranded on <body>). setTimeout(0) matches the Drawer's own focus-into
  // cadence and runs after captureOpener. Clean up on unmount.
  useEffect(() => {
    const id = setTimeout(() => {
      rootRef.current?.focus();
    }, 0);
    return () => clearTimeout(id);
  }, []);

  return (
    <div
      ref={setRootRef}
      tabIndex={-1}
      className="bg-chariot-background-primary flex h-full flex-col p-6 outline-none"
    >
      <h1 className="text-chariot-text-primary mb-6 text-2xl font-bold">
        Connect to Guard
      </h1>
      <div className="flex flex-row justify-between">
        <div className="w-72">
          <SearchBar
            placeholder="Search integrations"
            value={search}
            onChange={handleSearchChange}
            size="sm"
          />
        </div>
        <div className="bg-chariot-background-secondary text-chariot-text-primary flex items-center gap-2 rounded-md p-2 text-sm">
          <LightBulbIcon className="text-chariot-brand-primary size-5" />
          <span>
            Tip: You can add multiple accounts under the same integration
          </span>
        </div>
      </div>
      <Container className="mt-6 flex flex-row p-0">
        <div className="border-chariot-stroke w-[300px] border-r p-4">
          <button
            onClick={() => setSelectedCategory('All')}
            className={twMerge(
              'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors',
              selectedCategory === 'All'
                ? 'bg-chariot-background-secondary text-chariot-text-primary'
                : 'hover:bg-chariot-transparent'
            )}
          >
            <span>All</span>
            <span className="text-xs">{totalCount}</span>
          </button>

          {sortedCategories
            .filter(category => category !== 'All')
            .map(category => (
              <button
                key={category}
                onClick={() =>
                  setSelectedCategory(category as IntegrationCategory)
                }
                className={twMerge(
                  'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors',
                  selectedCategory === category
                    ? 'bg-chariot-background-secondary text-chariot-text-primary'
                    : 'hover:bg-chariot-transparent'
                )}
              >
                <span>{category}</span>
                <span className="text-xs">
                  {categoryCounts[category as IntegrationCategory]}
                </span>
              </button>
            ))}
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-8">
            {filteredIntegrations.map(({ category, integrations }) =>
              integrations.length === 0 ? null : (
                <section key={category}>
                  <h2 className="text-chariot-text-primary mb-4 text-xl font-semibold">
                    {category}
                  </h2>
                  <div className="flex flex-wrap gap-4">
                    {integrations.map(integration => {
                      const isConnected = isIntegrationConnected(
                        integration.id
                      );
                      // The connected account record, if this is a real
                      // connection. Absent for the demo-org CrowdStrike
                      // pseudo-connection (see isIntegrationConnected).
                      const connectedAccount = connectedIntegrations.find(
                        conn => conn.member === integration.id
                      );
                      const isDemoConnection = isConnected && !connectedAccount;
                      // "Add another" only makes sense for multi-instance
                      // integrations; preserved for the demo pseudo-connection.
                      const showAddMenu =
                        isConnected &&
                        canManageIntegrations &&
                        (isDemoConnection || Boolean(integration.multiple));
                      return (
                        <div
                          key={integration.id}
                          className="border-chariot-stroke relative w-[280px] rounded-lg border px-4 pt-4"
                        >
                          {showAddMenu && (
                            <div className="absolute top-2 right-2">
                              <Dropdown
                                onClick={event => event.stopPropagation()}
                                startIcon={
                                  <EllipsisVerticalIcon className="size-5" />
                                }
                                styleType="none"
                                menu={{
                                  items: [
                                    {
                                      label: 'Add',
                                      onClick: () => handleConnect(integration),
                                    },
                                  ],
                                }}
                              />
                            </div>
                          )}
                          <div className="flex h-full flex-row items-start gap-4 pb-4">
                            <img
                              src={`/icons/${currentTheme}/${integration.logo}`}
                              alt={integration.name}
                              className="size-8"
                            />
                            <div className="flex h-full flex-1 flex-col">
                              <h3 className="text-chariot-text-primary text-lg font-semibold">
                                {integration.name}
                              </h3>
                              <p className="text-chariot-text-primary mb-4 flex-1 items-start justify-start text-sm">
                                {integration.description}
                              </p>
                              {!isConnected ? (
                                <Button
                                  size="sm"
                                  disabled={!canManageIntegrations}
                                  onClick={async () => {
                                    await handleConnect(integration);
                                  }}
                                  data-testid={`integration-${integration.id}-connect`}
                                >
                                  Integrate
                                </Button>
                              ) : isDemoConnection ? (
                                // Demo-org CrowdStrike pseudo-connection —
                                // preserve the existing non-actionable state.
                                <Button
                                  variant="ghost"
                                  className="bg-green-500 font-medium text-white hover:bg-green-600"
                                  size="sm"
                                  data-testid={`integration-${integration.id}-connected`}
                                >
                                  Connected
                                </Button>
                              ) : integration.multiple ? (
                                <Button
                                  variant="ghost"
                                  className="bg-green-500 font-medium text-white hover:bg-green-600"
                                  size="sm"
                                  disabled={!canManageIntegrations}
                                  onClick={async () => {
                                    await handleConnect(integration);
                                  }}
                                  data-testid={`integration-${integration.id}-add-another`}
                                >
                                  Integrate Another
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={!canManageIntegrations}
                                  onClick={() =>
                                    connectedAccount &&
                                    setConfirmDisconnect(connectedAccount)
                                  }
                                  data-testid={`integration-${integration.id}-disconnect`}
                                >
                                  Disconnect
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )
            )}
          </div>
        </div>
      </Container>

      {/* New integration setup modal */}
      {newSetupIntegration && (
        <IntegrationSetupModal
          integration={newSetupIntegration}
          isOpen={true}
          onClose={() => setNewSetupIntegration(null)}
          onSuccess={() => {
            // Refresh integrations list
            setNewSetupIntegration(null);
            // Show success message
            // TODO: Add toast notification
          }}
        />
      )}

      {/* Old integration setup modal */}
      {setupModalIntegration &&
        (setupModalIntegration.inputs || setupModalIntegration.Markup ? (
          <SetupModal
            isOpen={Boolean(setupModalIntegration)}
            onClose={() => setSetupModalIntegration(null)}
            onComplete={() => setSetupModalIntegration(null)}
            selectedIntegration={undefined}
            formData={{}}
            integration={setupModalIntegration}
            onSwitchIntegration={setSetupModalIntegration}
          />
        ) : (
          <WaitlistedModal
            open={Boolean(setupModalIntegration)}
            onClose={() => setSetupModalIntegration(null)}
            onComplete={() => setSetupModalIntegration(null)}
            integration={setupModalIntegration}
          />
        ))}

      {/* Disconnect confirmation modal (shared with the /integrations page) */}
      <DisconnectIntegrationModal
        account={confirmDisconnect}
        onClose={() => setConfirmDisconnect(null)}
      />
    </div>
  );
};
